import { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CalendarX,
  Clock,
  ClockCountdown,
  Check,
  Checks,
  X,
  SealCheck,
  Stethoscope,
  Door,
  CurrencyCircleDollar,
  Receipt,
  FileText,
  Repeat,
  NotePencil,
  PencilSimple,
  Info,
  ArrowSquareOut,
  WarningCircle,
  CheckCircle,
  UserCircle,
  DotOutline,
} from "@phosphor-icons/react";
import EmptyDraw from "../ui/EmptyDraw";
import {
  selectClinicId,
  selectAllowMultipleTherapists,
  selectCanAccessFinance,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { useGetAppointmentsQuery } from "../../store/services/calendarApi";
import { useGetRoomsQuery } from "../../store/services/roomsApi";
import { TRANSACTION_STATUS } from "../../firebase";
import AppointmentModal from "../calendar/AppointmentEntryModal";
import { NoAccessState } from "../shared/access";
import "./PatientAppointmentsDiaryTab.css";
import ContentLoader from "../shared/ContentLoader";

const HEBREW_MONTHS = [
  "ינואר",
  "פברואר",
  "מרץ",
  "אפריל",
  "מאי",
  "יוני",
  "יולי",
  "אוגוסט",
  "ספטמבר",
  "אוקטובר",
  "נובמבר",
  "דצמבר",
];

const HEBREW_WEEKDAYS = [
  "ראשון",
  "שני",
  "שלישי",
  "רביעי",
  "חמישי",
  "שישי",
  "שבת",
];

// ============================================================================
// HELPERS
// ============================================================================

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "";
  return `₪${Number(amount).toLocaleString("he-IL", {
    maximumFractionDigits: 0,
  })}`;
};

const formatTime = (date) =>
  date.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

const formatMonthYear = (date) =>
  `${HEBREW_MONTHS[date.getMonth()]} ${date.getFullYear()}`;

const formatRelative = (date, now) => {
  const ms = date.getTime() - now.getTime();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));
  if (days === 0) return "היום";
  if (days === 1) return "מחר";
  if (days === -1) return "אתמול";
  if (days > 1 && days <= 7) return `בעוד ${days} ימים`;
  if (days < -1 && days >= -7) return `לפני ${Math.abs(days)} ימים`;
  return null;
};

/**
 * Derive the "visual" status for an appointment, similar to AppointmentCard.
 * Returns { key, label, color, Icon }
 */
const getAppointmentStatus = (apt, nowMs) => {
  const endMs = apt.endTime ? new Date(apt.endTime).getTime() : null;
  const isCancelled = apt.status === "cancelled";
  const isEnded =
    apt.status === "completed" || (endMs !== null && endMs < nowMs);

  if (isCancelled) {
    return { key: "cancelled", label: "בוטלה", color: "red", Icon: X };
  }

  if (isEnded) {
    const fullyHandled =
      apt.isApprovedByTherapist === true &&
      apt.isTherapistSummarizedTheMeeting === true &&
      apt.isTherapistUpdatedPaymentStatus === true;

    if (fullyHandled) {
      return {
        key: "completed",
        label: "הושלמה",
        color: "blue",
        Icon: SealCheck,
      };
    }
    if (apt.isApprovedByTherapist === true) {
      return {
        key: "approved",
        label: "אושרה",
        color: "teal",
        Icon: CheckCircle,
      };
    }
    return {
      key: "awaiting",
      label: "ממתינה לעדכון",
      color: "orange",
      Icon: ClockCountdown,
    };
  }

  // Future
  if (
    apt.whatsappInitialDeclined === true ||
    apt.whatsappReminderDeclined === true
  ) {
    return { key: "declined", label: "לא אושרה", color: "red", Icon: X };
  }
  if (
    apt.whatsappInitialConfirmed === true &&
    apt.whatsappReminderConfirmed === true
  ) {
    return {
      key: "confirmed_both",
      label: "אישור כפול",
      color: "teal",
      Icon: Checks,
    };
  }
  if (
    apt.whatsappInitialConfirmed === true ||
    apt.whatsappReminderConfirmed === true
  ) {
    return { key: "confirmed", label: "אושרה", color: "teal", Icon: Check };
  }
  return {
    key: "scheduled",
    label: "מתוכננת",
    color: "primary",
    Icon: Clock,
  };
};

/**
 * Aggregate payment info for an appointment from its linked transactions.
 */
const getPaymentSummary = (apt, txsForApt, nowMs) => {
  const isFuture = new Date(apt.startTime).getTime() > nowMs;
  const isCancelled = apt.status === "cancelled";

  // Invoice detection (legacy appointment-level or from any tx)
  const txWithInvoice = txsForApt.find(
    (tx) =>
      tx.status === TRANSACTION_STATUS.INVOICE_MANUAL ||
      tx.status === TRANSACTION_STATUS.INVOICE_AUTO ||
      (tx.RecepitsDocsRefArray && tx.RecepitsDocsRefArray.length > 0),
  );
  const invoiceFromAppt =
    apt.RecepitsDocsRefArray && apt.RecepitsDocsRefArray.length > 0
      ? apt.RecepitsDocsRefArray[0]
      : null;
  const invoiceRef = txWithInvoice?.RecepitsDocsRefArray?.[0] || invoiceFromAppt;
  const hasInvoice =
    !!invoiceRef ||
    apt.isProducedReceipitForThisMeeting === true ||
    !!txWithInvoice;

  if (!txsForApt.length) {
    if (isCancelled) return null;
    if (isFuture) {
      return {
        key: "expected",
        label: "תשלום צפוי",
        color: "gray",
        amount: apt.paymentAmount ?? null,
        hasInvoice,
        invoiceRef,
      };
    }
    return {
      key: "missing",
      label: "אין תשלום רשום",
      color: "warning",
      amount: apt.paymentAmount ?? null,
      hasInvoice,
      invoiceRef,
    };
  }

  // Prefer the "best" transaction status for display
  const priority = {
    [TRANSACTION_STATUS.INVOICE_AUTO]: 7,
    [TRANSACTION_STATUS.INVOICE_MANUAL]: 6,
    [TRANSACTION_STATUS.PAID]: 5,
    [TRANSACTION_STATUS.PENDING_COLLECTION]: 4,
    [TRANSACTION_STATUS.PENDING]: 4,
    [TRANSACTION_STATUS.SPLIT]: 3,
    [TRANSACTION_STATUS.EXPECTED]: 2,
    [TRANSACTION_STATUS.REFUNDED]: 1,
    [TRANSACTION_STATUS.CANCELLED]: 0,
  };
  const ranked = [...txsForApt].sort(
    (a, b) => (priority[b.status] || 0) - (priority[a.status] || 0),
  );
  const primary = ranked[0];

  // Sum paid (including invoiced)
  const paidTotal = txsForApt
    .filter(
      (tx) =>
        tx.status === TRANSACTION_STATUS.PAID ||
        tx.status === TRANSACTION_STATUS.INVOICE_MANUAL ||
        tx.status === TRANSACTION_STATUS.INVOICE_AUTO,
    )
    .reduce((sum, tx) => sum + (tx.actualPaidAmount ?? tx.amount ?? 0), 0);

  let info;
  switch (primary.status) {
    case TRANSACTION_STATUS.PAID:
      info = { key: "paid", label: "שולם", color: "green" };
      break;
    case TRANSACTION_STATUS.INVOICE_MANUAL:
    case TRANSACTION_STATUS.INVOICE_AUTO:
      info = { key: "invoice", label: "חשבונית", color: "purple" };
      break;
    case TRANSACTION_STATUS.PENDING:
    case TRANSACTION_STATUS.PENDING_COLLECTION:
      info = { key: "pending", label: "ממתין לגבייה", color: "warning" };
      break;
    case TRANSACTION_STATUS.EXPECTED:
      info = { key: "expected", label: "תשלום צפוי", color: "gray" };
      break;
    case TRANSACTION_STATUS.SPLIT:
      info = { key: "split", label: "פיצול", color: "blue" };
      break;
    case TRANSACTION_STATUS.REFUNDED:
      info = { key: "refunded", label: "זיכוי", color: "red" };
      break;
    case TRANSACTION_STATUS.CANCELLED:
      info = { key: "cancelled_tx", label: "תשלום בוטל", color: "red" };
      break;
    default:
      info = { key: "unknown", label: "תשלום", color: "gray" };
  }

  return {
    ...info,
    amount: paidTotal > 0 ? paidTotal : primary.amount,
    hasInvoice,
    invoiceRef,
    txsCount: txsForApt.length,
  };
};

// ============================================================================
// LEDGER ROW
// ============================================================================

function AppointmentTimelineRow({
  apt,
  nowMs,
  treatment,
  txs,
  therapistName,
  treatmentTypeName,
  roomName,
  seriesCount,
  showTherapistColumn,
  canEdit,
  canWriteTreatments,
  canAccessFinance,
  onOpenModal,
  onAddSummary,
  onMarkPaid,
  onOpenTreatment,
}) {
  const start = new Date(apt.startTime);
  const end = apt.endTime ? new Date(apt.endTime) : null;
  const isCancelled = apt.status === "cancelled";
  const isFuture = start.getTime() > nowMs;
  const isEnded =
    apt.status === "completed" || (end && end.getTime() < nowMs);
  const status = getAppointmentStatus(apt, nowMs);
  const payment = getPaymentSummary(apt, txs, nowMs);
  const relative = formatRelative(start, new Date(nowMs));

  const hasSummary = !!treatment;
  const shouldShowSummaryBadge = isEnded || hasSummary;
  const canAddSummary =
    canEdit && canWriteTreatments !== false && !isCancelled && isEnded && !hasSummary;
  const canMarkPaid =
    canEdit &&
    canAccessFinance &&
    !isCancelled &&
    isEnded &&
    (!payment || payment.key === "missing");

  const StatusIcon = status.Icon;

  const durationMins = end
    ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000))
    : null;

  const handleOpenInvoice = (e) => {
    e.stopPropagation();
    if (payment?.invoiceRef?.documentUrl) {
      window.open(payment.invoiceRef.documentUrl, "_blank", "noopener");
    } else if (payment?.invoiceRef?.url) {
      window.open(payment.invoiceRef.url, "_blank", "noopener");
    }
  };

  const hasMeta =
    treatmentTypeName ||
    (showTherapistColumn && therapistName) ||
    roomName ||
    apt.notes;

  const hasChips =
    (apt.recurringGroupId && apt.recurringIndex) ||
    shouldShowSummaryBadge ||
    payment ||
    payment?.hasInvoice;

  const showActions =
    !isCancelled && (canAddSummary || canMarkPaid || (isFuture && canEdit));

  return (
    <div
      className={`pdiary-row ${isCancelled ? "is-cancelled" : ""}`}
      onClick={() => onOpenModal(apt)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenModal(apt);
        }
      }}
    >
      <div className="pdiary-row-main">
        <div className="pdiary-row-top">
          <span className={`pdiary-pill pill-${status.color}`}>
            <StatusIcon size={11} weight="bold" />
            {status.label}
          </span>
          <span className="pdiary-row-date">
            {HEBREW_WEEKDAYS[start.getDay()]}, {start.getDate()} ב
            {HEBREW_MONTHS[start.getMonth()]}
          </span>
          <span className="pdiary-row-time">
            {formatTime(start)}
            {end ? `–${formatTime(end)}` : ""}
            {durationMins ? ` · ${durationMins} דק׳` : ""}
          </span>
          {relative && <span className="pdiary-relative">{relative}</span>}
        </div>

        {hasMeta && (
          <div className="pdiary-row-meta">
            {treatmentTypeName && (
              <span className="pdiary-meta-item">
                <Stethoscope size={11} weight="fill" />
                {treatmentTypeName}
              </span>
            )}
            {showTherapistColumn && therapistName && (
              <span className="pdiary-meta-item">
                <UserCircle size={11} weight="fill" />
                {therapistName}
              </span>
            )}
            {roomName && (
              <span className="pdiary-meta-item">
                <Door size={11} weight="fill" />
                {roomName}
              </span>
            )}
            {apt.notes && (
              <span className="pdiary-meta-item" title={apt.notes}>
                <Info size={11} weight="fill" />
                {apt.notes.length > 40
                  ? `${apt.notes.slice(0, 40)}…`
                  : apt.notes}
              </span>
            )}
          </div>
        )}

        {isCancelled && apt.cancellationReason && (
          <div
            className="pdiary-cancel-reason"
            title={apt.cancellationReason}
          >
            <CalendarX size={11} weight="fill" />
            <span>סיבת ביטול: {apt.cancellationReason}</span>
          </div>
        )}

        {hasChips && (
          <div className="pdiary-row-chips">
            {apt.recurringGroupId && apt.recurringIndex && (
              <span
                className="pdiary-chip chip-muted"
                title={
                  seriesCount
                    ? `סדרה בת ${apt.recurringTotalCount || seriesCount} מפגשים`
                    : "פגישה בסדרה"
                }
              >
                <Repeat size={11} weight="bold" />
                סדרה {apt.recurringIndex}
                {apt.recurringTotalCount
                  ? `/${apt.recurringTotalCount}`
                  : ""}
              </span>
            )}

            {shouldShowSummaryBadge && (
              <button
                className={`pdiary-chip ${
                  hasSummary ? "chip-link" : "chip-warning"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasSummary) onOpenTreatment(treatment);
                  else if (canAddSummary) onAddSummary(apt);
                }}
                title={
                  hasSummary
                    ? "פתח סיכום טיפול"
                    : canAddSummary
                      ? "הוסף סיכום טיפול"
                      : "חסר סיכום טיפול"
                }
              >
                {hasSummary ? (
                  <>
                    <FileText size={11} weight="fill" />
                    סיכום טיפול
                    {treatment?.status === "draft" ? " (טיוטה)" : ""}
                  </>
                ) : (
                  <>
                    <NotePencil size={11} weight="fill" />
                    חסר סיכום
                  </>
                )}
              </button>
            )}

            {payment && (
              <span
                className={`pdiary-chip chip-${payment.color}`}
                title={
                  payment.txsCount > 1
                    ? `${payment.txsCount} תנועות כספיות`
                    : payment.label
                }
              >
                <CurrencyCircleDollar size={11} weight="fill" />
                {payment.label}
                {payment.amount ? ` · ${formatCurrency(payment.amount)}` : ""}
              </span>
            )}

            {payment?.hasInvoice && (
              <span
                className={`pdiary-chip chip-invoice ${
                  payment.invoiceRef ? "clickable" : ""
                }`}
                onClick={payment.invoiceRef ? handleOpenInvoice : undefined}
                role={payment.invoiceRef ? "button" : undefined}
                tabIndex={payment.invoiceRef ? 0 : undefined}
                title={
                  payment.invoiceRef
                    ? "פתח חשבונית"
                    : "הופקה חשבונית עבור פגישה זו"
                }
              >
                <Receipt size={11} weight="fill" />
                חשבונית
                {payment.invoiceRef?.documentNumber
                  ? ` #${payment.invoiceRef.documentNumber}`
                  : ""}
                {payment.invoiceRef && (
                  <ArrowSquareOut size={10} weight="bold" />
                )}
              </span>
            )}
          </div>
        )}
      </div>

      {showActions && (
        <div className="pdiary-row-side">
          {canAddSummary && (
            <button
              className="pdiary-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onAddSummary(apt);
              }}
            >
              <NotePencil size={14} weight="bold" />
              הוסף סיכום טיפול
            </button>
          )}
          {canMarkPaid && (
            <button
              className={`pdiary-action-btn ${canAddSummary ? "soft" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onMarkPaid(apt);
              }}
            >
              <Check size={14} weight="bold" />
              סמן כשולם
            </button>
          )}
          {isFuture && canEdit && (
            <button
              className="pdiary-action-btn soft"
              onClick={(e) => {
                e.stopPropagation();
                onOpenModal(apt);
              }}
            >
              <PencilSimple size={14} weight="bold" />
              שנה / בטל
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function PatientAppointmentsDiaryTab({
  patient,
  patientId,
  canEdit,
  treatments = [],
  transactions = [],
  clinicTherapists = [],
  treatmentTypes = [],
  onMarkAsPaid,
  // Appointment rules: owner || admin || co-therapist || calendar.viewClinic ||
  // all-mode. The parent computes this (it has the patient doc + selectors);
  // defaults to true so other callers keep today's behavior.
  canViewAppointments = true,
}) {
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const allowMultipleTherapists = useSelector(selectAllowMultipleTherapists);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  // treatments.write — legacy therapists resolve TRUE; explicit false hides the new-report CTA
  const canWriteTreatments = useSelector(selectHasPermission("treatments.write"));

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Ledger display filter (presentation only)
  const [diaryFilter, setDiaryFilter] = useState("all");

  // Wide date range covering the patient's full history + future scheduling.
  const dateRange = useMemo(() => {
    const start = new Date();
    start.setFullYear(start.getFullYear() - 10);
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setFullYear(end.getFullYear() + 5);
    end.setHours(23, 59, 59, 999);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, []);

  const {
    data: appointments = [],
    isLoading,
    error,
  } = useGetAppointmentsQuery(
    {
      clinicId,
      patientId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    },
    {
      // Skip when the viewer can't read this patient's appointments — the
      // query would be rejected by rules (permission-denied).
      skip: !clinicId || !patientId || !canViewAppointments,
      // Always refetch when the tab mounts — guarantees fresh data after
      // the user cancels/edits an appointment elsewhere (calendar, pending
      // appointments carousel) and immediately navigates into the patient
      // profile. Without this the tag-invalidation refetch can race the
      // navigation, leaving the diary showing stale rows.
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: clinicRooms = [] } = useGetRoomsQuery(
    { clinicId },
    { skip: !clinicId },
  );

  // ---- Lookups ----
  const treatmentByAppointmentId = useMemo(() => {
    const map = new Map();
    for (const t of treatments) {
      // Soft-deleted reports don't count as documentation
      if (t.appointmentId && t.isDeleted !== true) map.set(t.appointmentId, t);
    }
    return map;
  }, [treatments]);

  const transactionsByAppointmentId = useMemo(() => {
    const map = new Map();
    for (const tx of transactions) {
      if (!tx.appointmentId) continue;
      if (!map.has(tx.appointmentId)) map.set(tx.appointmentId, []);
      map.get(tx.appointmentId).push(tx);
    }
    return map;
  }, [transactions]);

  const roomNameById = useMemo(() => {
    const map = new Map();
    for (const r of clinicRooms) map.set(r.id, r.name);
    return map;
  }, [clinicRooms]);

  const therapistNameById = useMemo(() => {
    const map = new Map();
    for (const t of clinicTherapists) {
      map.set(t.uid, `${t.firstName} ${t.lastName}`);
    }
    return map;
  }, [clinicTherapists]);

  const typeNameById = useMemo(() => {
    const map = new Map();
    for (const t of treatmentTypes) map.set(t.id, t.name);
    return map;
  }, [treatmentTypes]);

  // Chronological order: oldest → newest (single continuous timeline)
  const chronological = useMemo(() => {
    return [...appointments].sort(
      (a, b) => new Date(a.startTime) - new Date(b.startTime),
    );
  }, [appointments]);

  const nowMs = useMemo(() => Date.now(), [appointments]);

  // Series sizes (for tooltip)
  const seriesSizeByGroupId = useMemo(() => {
    const map = new Map();
    for (const apt of chronological) {
      if (!apt.recurringGroupId) continue;
      map.set(apt.recurringGroupId, (map.get(apt.recurringGroupId) || 0) + 1);
    }
    return map;
  }, [chronological]);

  // KPIs
  const kpis = useMemo(() => {
    let total = 0,
      completed = 0,
      cancelled = 0,
      upcoming = 0;
    for (const apt of chronological) {
      total++;
      const startMs = new Date(apt.startTime).getTime();
      const endMs = apt.endTime ? new Date(apt.endTime).getTime() : null;
      if (apt.status === "cancelled") {
        cancelled++;
      } else if (
        apt.status === "completed" ||
        (endMs !== null && endMs < nowMs)
      ) {
        completed++;
      }
      if (startMs >= nowMs && apt.status !== "cancelled") upcoming++;
    }
    return { total, completed, cancelled, upcoming };
  }, [chronological, nowMs]);

  // Show therapist column in multi-therapist clinics OR if appointments span multiple therapists
  const showTherapistColumn = useMemo(() => {
    if (allowMultipleTherapists) return true;
    const set = new Set(chronological.map((a) => a.therapistId).filter(Boolean));
    return set.size > 1;
  }, [chronological, allowMultipleTherapists]);

  // ---- Handlers ----
  const handleOpenModal = useCallback((apt) => {
    setSelectedAppointment(apt);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  }, []);

  const handleAddSummary = useCallback(
    (apt) => {
      navigate(
        `/dashboard/patients/${patientId}/treatments/new?appointmentId=${apt.id}`,
      );
    },
    [navigate, patientId],
  );

  const handleOpenTreatment = useCallback(
    (treatment) => {
      if (!treatment) return;
      navigate(
        `/dashboard/patients/${patientId}/treatments/${treatment.id}`,
      );
    },
    [navigate, patientId],
  );

  const handleMarkPaid = useCallback(
    (apt) => {
      if (typeof onMarkAsPaid === "function") onMarkAsPaid(apt);
    },
    [onMarkAsPaid],
  );

  // ---- Display filter (presentation only) ----
  const visibleAppointments = useMemo(() => {
    if (diaryFilter === "all") return chronological;
    return chronological.filter((apt) => {
      const startMs = new Date(apt.startTime).getTime();
      const endMs = apt.endTime ? new Date(apt.endTime).getTime() : null;
      const isCancelled = apt.status === "cancelled";
      const isEnded =
        apt.status === "completed" || (endMs !== null && endMs < nowMs);
      if (diaryFilter === "cancelled") return isCancelled;
      if (diaryFilter === "completed") return !isCancelled && isEnded;
      if (diaryFilter === "upcoming") return !isCancelled && startMs >= nowMs;
      return true;
    });
  }, [chronological, diaryFilter, nowMs]);

  // ---- Unified timeline items: month dividers + today marker + rows ----
  const timelineItems = useMemo(() => {
    const items = [];
    let lastMonthKey = null;
    let todayInserted = false;

    for (const apt of visibleAppointments) {
      const start = new Date(apt.startTime);
      const startMs = start.getTime();

      // Insert "today" divider right before the first future appointment
      if (!todayInserted && startMs >= nowMs) {
        items.push({ type: "today", key: "today-divider" });
        todayInserted = true;
      }

      const monthKey = `${start.getFullYear()}-${start.getMonth()}`;
      if (monthKey !== lastMonthKey) {
        items.push({
          type: "month",
          key: `m-${monthKey}`,
          label: formatMonthYear(start),
        });
        lastMonthKey = monthKey;
      }

      items.push({ type: "row", key: `a-${apt.id}`, apt });
    }

    // If all appointments are in the past, show today marker at the end
    if (!todayInserted && visibleAppointments.length > 0) {
      items.push({ type: "today", key: "today-divider" });
    }

    return items;
  }, [visibleAppointments, nowMs]);

  const renderRow = (apt) => {
    const therapistName =
      apt.therapistName ||
      therapistNameById.get(apt.therapistId) ||
      null;
    const treatmentTypeName =
      typeNameById.get(apt.treatmentTypeId) ||
      apt.treatmentTypeName ||
      null;
    const roomName = apt.roomId ? roomNameById.get(apt.roomId) : null;
    const seriesCount = apt.recurringGroupId
      ? seriesSizeByGroupId.get(apt.recurringGroupId)
      : 0;
    const treatment = treatmentByAppointmentId.get(apt.id);
    const txs = transactionsByAppointmentId.get(apt.id) || [];

    return (
      <AppointmentTimelineRow
        key={apt.id}
        apt={apt}
        nowMs={nowMs}
        treatment={treatment}
        txs={txs}
        therapistName={therapistName}
        treatmentTypeName={treatmentTypeName}
        roomName={roomName}
        seriesCount={seriesCount}
        showTherapistColumn={showTherapistColumn}
        canEdit={canEdit}
        canWriteTreatments={canWriteTreatments}
        canAccessFinance={canAccessFinance}
        onOpenModal={handleOpenModal}
        onAddSummary={handleAddSummary}
        onMarkPaid={handleMarkPaid}
        onOpenTreatment={handleOpenTreatment}
      />
    );
  };

  // ---- No access / loading / error / empty ----
  if (!canViewAppointments) {
    return (
      <NoAccessState
        reason="הרשאה נדרשת: צפייה ביומן הקליניקה"
        size="panel"
      />
    );
  }

  if (isLoading) {
    return (
      <ContentLoader fill message="טוען יומן פגישות…" />
    );
  }

  if (error) {
    console.error("[CK-2305] Error loading patient appointments diary:", {
      errorCode: "CK-2305",
      errorMessage: error?.message,
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
    return (
      <div className="pdiary-state">
        <WarningCircle size={24} weight="fill" className="pdiary-state-icon" />
        <h4>שגיאה בטעינת הפגישות</h4>
        <p>נסה לרענן את העמוד. אם הבעיה נמשכת, פנה לתמיכה.</p>
      </div>
    );
  }

  if (!chronological.length) {
    return (
      <div className="pdiary-tab">
        <div className="pdiary-state apt-diary-empty">
          <EmptyDraw variant="calendar" />
          <h4>עוד אין פגישות למטופל הזה</h4>
          <p>
            כשתקבע פגישה ראשונה ביומן, היא תופיע כאן יחד עם היסטוריית
            הסיכומים, התשלומים והחשבוניות — הכול במקום אחד.
          </p>
        </div>
      </div>
    );
  }

  const filterOptions = [
    { id: "all", label: "הכל", count: kpis.total },
    { id: "upcoming", label: "עתידיות", count: kpis.upcoming },
    { id: "completed", label: "הושלמו", count: kpis.completed },
    { id: "cancelled", label: "בוטלו", count: kpis.cancelled },
  ];

  return (
    <div className="pdiary-tab">
      {/* Compact summary strip */}
      <div className="pdiary-summary">
        <div className="pdiary-stat total">
          <span className="pdiary-stat-value">{kpis.total}</span>
          <span className="pdiary-stat-label">
            <span className="pdiary-stat-dot" />
            סה״כ פגישות
          </span>
        </div>
        <div className="pdiary-stat completed">
          <span className="pdiary-stat-value">{kpis.completed}</span>
          <span className="pdiary-stat-label">
            <span className="pdiary-stat-dot" />
            הושלמו
          </span>
        </div>
        <div className="pdiary-stat upcoming">
          <span className="pdiary-stat-value">{kpis.upcoming}</span>
          <span className="pdiary-stat-label">
            <span className="pdiary-stat-dot" />
            עתידיות
          </span>
        </div>
        <div className="pdiary-stat cancelled">
          <span className="pdiary-stat-value">{kpis.cancelled}</span>
          <span className="pdiary-stat-label">
            <span className="pdiary-stat-dot" />
            בוטלו
          </span>
        </div>
      </div>

      {/* Filter chips */}
      <div className="pdiary-filters">
        {filterOptions.map((option) => (
          <button
            key={option.id}
            className={`pdiary-filter-chip ${
              diaryFilter === option.id ? "active" : ""
            }`}
            onClick={() => setDiaryFilter(option.id)}
          >
            {option.label}
            {option.count > 0 && (
              <span className="pdiary-filter-count">{option.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Unified chronological ledger: oldest → newest, today marker in between */}
      <div className="pdiary-ledger">
        {visibleAppointments.length === 0 ? (
          <div className="pdiary-filter-empty">אין פגישות בסינון שנבחר</div>
        ) : (
          timelineItems.map((item) => {
            if (item.type === "month") {
              return (
                <div className="pdiary-month-divider" key={item.key}>
                  {item.label}
                </div>
              );
            }
            if (item.type === "today") {
              const todayDate = new Date(nowMs);
              return (
                <div className="pdiary-today-divider" key={item.key}>
                  <span className="pdiary-today-line" />
                  <span className="pdiary-today-pill">
                    <DotOutline size={14} weight="fill" />
                    היום ·{" "}
                    {todayDate.toLocaleDateString("he-IL", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                  <span className="pdiary-today-line" />
                </div>
              );
            }
            return renderRow(item.apt);
          })
        )}
      </div>

      {/* Appointment Modal */}
      {isModalOpen && selectedAppointment && (
        <AppointmentModal
          appointment={selectedAppointment}
          patients={patient ? [patient] : []}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export default PatientAppointmentsDiaryTab;
